import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ProjectHistoryService } from '../../services/project-history.service';
import { forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea as InputTextareaModule } from 'primeng/inputtextarea';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AgendaCas, AgendaCasService } from '../../services/agenda-cas.service';
import { AuthService } from '../../../../shared/service/auth.service';
import { UserService, User } from '../../../../shared/service/user.service';
import { AdminService } from '../../services/admin.service';
import { RingoverService } from '../../../../shared/services/ringover.service';
import { origines as origineOptions } from '../../../cac/models/enum';
import { STATUT_LEAD_OPTIONS } from '../cas-contacts/cas-contacts.component';

@Component({
  selector: 'app-agenda-cas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './agenda-cas.component.html',
  styleUrl: './agenda-cas.component.css',
  providers: [ConfirmationService]
})
export class AgendaCasComponent implements OnInit {
  agendaProjects: AgendaCas[] = [];
  loading = false;
  convertingId: number | null = null;
  deletingId: number | null = null;
  searchTerm = '';
  selectedStatus: string | null = null;
  selectedUserId: number | null = null;
  totalRecords = 0;
  rows = 10;
  first = 0;
  showDetailDialog = false;
  selectedAgendaCas: AgendaCas | null = null;
  todayOnly = false;
  editingItemId: number | null = null;
  editStartAt = '';
  savingDateId: number | null = null;

  // Édition inline du commentaire
  editingCommentId: number | null = null;
  editComment = '';
  savingCommentId: number | null = null;

  // Édition inline du statut
  savingLeadStatusId: number | null = null;

  // Vue archivée : affiche uniquement les RDV archivés
  showArchived = false;
  archivingId: number | null = null;
  archivingSelection = false;

  // Commentaire (source : Transaction.commentaire)
  commentDialogVisible = false;
  commentDialogTitle = '';
  commentDialogText = '';

  isAdmin = false;
  currentUserId: number | null = null;
  userOptions: Array<{ label: string; value: number }> = [];

  statusOptions = STATUT_LEAD_OPTIONS.map(status => ({ label: status, value: status }));

  origineFilter = origineOptions;

  // Sélection multiple
  selectedProjects: AgendaCas[] = [];

  // Actions groupées
  selectedAction: string | null = null;
  selectedBulkStatus: string | null = null;
  selectedAssignUserId: number | null = null;
  updatingStatus = false;
  assigning = false;
  deletingSelection = false;
  exportingExcel = false;

  sendingSmsId: number | null = null;
  agentProfile: User | null = null;

  constructor(
    private agendaCasService: AgendaCasService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private adminService: AdminService,
    private ringoverService: RingoverService,
    private projectHistoryService: ProjectHistoryService
  ) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.currentUserId = this.getCurrentUserIdFromToken();
    if (this.isAdmin) {
      this.loadUsers();
    }
    this.loadAgendaCass();
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return null;
    }
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const decoded = JSON.parse(atob(payload));
      const userId = Number(decoded?.user_id ?? decoded?.userId);
      return Number.isFinite(userId) && userId > 0 ? userId : null;
    } catch (error) {
      console.warn('Impossible de décoder user_id depuis le token JWT:', error);
      return null;
    }
  }

  loadUsers(): void {
    this.adminService.getUsers(1, 100).subscribe({
      next: (response: any) => {
        // Handle both possible responses (UserList object or User array)
        const usersList = Array.isArray(response) ? response : (response.users || []);
        
        this.userOptions = usersList
          .filter((user: any) => user.status === 'ACTIVE' || user.is_active !== false)
          .map((user: any) => ({
            label: `${user.prenom || ''} ${user.nom || ''}`.trim() || user.email,
            value: user.id
          }));
      },
      error: (error: any) => {
        console.error('Erreur chargement conseillers:', error);
      }
    });
  }

  loadAgendaCass(offset: number = this.first, limit: number = this.rows): void {
    this.loading = true;
    this.first = offset;
    this.rows = limit;

    this.agendaCasService.getAgendaProjects(limit, offset, {
      search: this.searchTerm.trim() || undefined,
      status: this.selectedStatus || undefined,
      today_only: this.todayOnly || undefined,
      user_id: (this.isAdmin && this.selectedUserId != null) ? this.selectedUserId : undefined,
      archived: this.showArchived
    }).subscribe({
      next: (response: any) => {
        this.agendaProjects = response.items || [];
        this.totalRecords = response.total || 0;
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Erreur chargement RDV santé:', error);
        const backendMessage = error?.error?.detail || error?.error?.message;
        this.messageService.add({
          severity: 'error',
          summary: 'Chargement échoué',
          detail: backendMessage || (error?.status === 0
            ? 'Impossible de joindre le backend CAS. Vérifiez que le serveur Spring Boot est démarré.'
            : 'Impossible de récupérer les RDV santé.')
        });
        this.loading = false;
      }
    });
  }

  onPageChange(event: any): void {
    this.loadAgendaCass(event.first || 0, event.rows || this.rows);
  }

  onSearchChange(): void {
    this.loadAgendaCass(0, this.rows);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = null;
    this.selectedUserId = null;
    this.todayOnly = false;
    this.showArchived = false;
    this.loadAgendaCass(0, this.rows);
  }

  refresh(): void {
    this.loadAgendaCass(this.first, this.rows);
  }

  loadAgendaCas(item: AgendaCas): void {
    if (this.convertingId !== null) {
      return;
    }

    if (item.projectId) {
      this.openCompare(item);
      return;
    }

    this.convertingId = item.id;
    this.agendaCasService.createProjectFromAgenda(item.id).subscribe({
      next: (response) => {
        const updatedItem = response.agendaProject;
        this.agendaProjects = this.agendaProjects.map(project =>
          project.id === updatedItem.id ? updatedItem : project
        );
        this.openCompare(updatedItem, response.projectId);
      },
      error: (error: any) => {
        console.error('Erreur conversion RDV santé:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Création échouée',
          detail: error?.error?.detail || error?.error?.message || 'Impossible de créer le projet CAS depuis l\'agenda.'
        });
        this.convertingId = null;
      }
    });
  }
  confirmDelete(item: AgendaCas): void {
    this.confirmationService.confirm({
      message: `Supprimer le RDV santé de ${this.getClientName(item)} ?`,
      header: 'Supprimer le RDV santé',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAgendaProject(item)
    });
  }

  viewAgendaCas(item: AgendaCas): void {
    this.selectedAgendaCas = item;
    this.showDetailDialog = true;
  }

  closeDetailDialog(): void {
    this.showDetailDialog = false;
    this.selectedAgendaCas = null;
  }

  saveCommentaire(item: AgendaCas): void {
    if (!item || !item.id) return;
    this.agendaCasService.updateAgendaProject(item.id, { commentaire: item.commentaire } as any).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Succès', detail: 'Commentaire enregistré' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Échec de la sauvegarde du commentaire' });
      }
    });
  }

  // ---- Édition inline du commentaire ----
  startEditComment(item: AgendaCas): void {
    this.editingCommentId = item.id;
    this.editComment = item.commentaire || '';
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editComment = '';
  }

  saveComment(item: AgendaCas): void {
    if (this.savingCommentId !== null) return;
    this.savingCommentId = item.id;
    const newComment = this.editComment.trim();
    this.agendaCasService.updateAgendaProject(item.id, { commentaire: newComment } as any).subscribe({
      next: (updated: any) => {
        this.agendaProjects = this.agendaProjects.map(p => p.id === updated.id ? updated : p);
        this.editingCommentId = null;
        this.editComment = '';
        this.savingCommentId = null;
        this.messageService.add({ severity: 'success', summary: 'Commentaire enregistré', detail: 'Le commentaire du RDV a été mis à jour.' });
      },
      error: (error: any) => {
        console.error('Erreur mise à jour commentaire:', error);
        this.messageService.add({ severity: 'error', summary: 'Mise à jour échouée', detail: error?.error?.detail || 'Impossible de modifier le commentaire du RDV.' });
        this.savingCommentId = null;
      }
    });
  }

  onLeadStatusChange(item: AgendaCas, newStatus: string): void {
    if (this.savingLeadStatusId !== null) return;
    this.savingLeadStatusId = item.id;
    this.agendaCasService.updateAgendaProject(item.id, { status: newStatus } as any).subscribe({
      next: (updated: any) => {
        this.agendaProjects = this.agendaProjects.map(p => p.id === updated.id ? updated : p);
        this.savingLeadStatusId = null;
        this.messageService.add({ severity: 'success', summary: 'Statut mis à jour', detail: 'Le statut du RDV a été modifié.' });
      },
      error: (error: any) => {
        console.error('Erreur mise à jour statut:', error);
        this.messageService.add({ severity: 'error', summary: 'Mise à jour échouée', detail: error?.error?.detail || 'Impossible de modifier le statut du RDV.' });
        this.savingLeadStatusId = null;
      }
    });
  }

  deleteAgendaProject(item: AgendaCas): void {
    this.deletingId = item.id;
    this.agendaCasService.deleteAgendaProject(item.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Projet supprimé',
          detail: 'Le RDV santé a été supprimé.'
        });
        this.deletingId = null;
        this.refresh();
      },
      error: (error: any) => {
        console.error('Erreur suppression RDV santé:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Suppression échouée',
          detail: error?.error?.detail || 'Impossible de supprimer le RDV santé.'
        });
        this.deletingId = null;
      }
    });
  }

  getClientName(item: AgendaCas): string {
    const personName = [item.prenom, item.nom].filter(Boolean).join(' ').trim();
    return personName || item.title || 'Client agenda';
  }

  /** Nom affiché dans le tableau : civilité + NOM uniquement (ex: "MR Rezgui"). */
  getClientShortName(item: AgendaCas): string {
    const shortName = [item.civilite, item.nom].filter(Boolean).join(' ').trim();
    return shortName || item.nom || item.title || 'Client agenda';
  }

  getClientInitials(item: AgendaCas): string {
    const source = this.getClientName(item);
    return source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('') || 'A';
  }

  getVendeurName(item: AgendaCas): string {
    return item.ownerName || '-';
  }

  /** Nom de la personne ayant pris le RDV (premier nom dans le titre Google Agenda). */
  getRdvTakerName(item: AgendaCas): string | null {
    const fromVendeur = (item.nomVendeur || '').trim();
    if (fromVendeur) {
      return fromVendeur;
    }
    const title = (item.title || '').trim();
    if (!title) {
      return null;
    }
    const firstToken = title.split(/[\s,\-–|/]+/).filter(Boolean)[0];
    return firstToken || null;
  }

  /** Normalise une valeur de statut en gérant les anciennes valeurs (nouveau/converti). */
  private normalizeStatusValue(status: string | null | undefined): string {
    if (!status) {
      return '';
    }
    const normalized = status.trim().toLowerCase();
    if (normalized === 'nouveau') {
      return 'a_traiter';
    }
    if (normalized === 'converti') {
      return 'traite';
    }
    return normalized;
  }

  getStatusLabel(status: string | null | undefined): string {
    const normalized = this.normalizeStatusValue(status);
    const match = this.statusOptions.find(option => option.value === normalized);
    return match?.label || status || 'À traiter';
  }

  getStatusSeverity(status: string | null | undefined): string {
    switch (this.normalizeStatusValue(status)) {
      case 'traite':
        return 'success';
      case 'a_traiter':
        return 'info';
      case 'nrp':
        return 'warning';
      case 'a_supprimer':
        return 'danger';
      case 'pas_interesse':
        return 'secondary';
      default:
        return 'info';
    }
  }

  // ---- Mapping origine (aligné sur Project History) ----
  getOrigineColor(origine: string | null | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (this.normalizeOrigineValue(origine)) {
      case 'FOURNISSEUR_DE_LEAD': return 'success';
      case 'RDV_LBC': return 'info';
      case 'RDV_DIRECT': return 'info';
      case 'TELEPHONE': return 'warning';
      case 'MultiEquipement': return 'warning';
      case 'INTERNET': return 'success';
      case 'COURTIER': return 'info';
      case 'PARRAINAGE': return 'warning';
      case 'Doublon': return 'danger';
      default: return 'secondary';
    }
  }

  getOrigineLabel(origine: string | null | undefined): string {
    const normalizedOrigine = this.normalizeOrigineValue(origine);
    const option = this.origineFilter.find((item: any) => item.value === normalizedOrigine);
    if (option) {
      return option.label;
    }
    return origine || 'Non spécifié';
  }

  private normalizeOrigineValue(origine: string | null | undefined): string {
    if (!origine) {
      return '';
    }
    const normalized = origine
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

    const aliases: Record<string, string> = {
      COMPARADISE: 'FOURNISSEUR_DE_LEAD',
      'LEAD FOURNISSEUR': 'FOURNISSEUR_DE_LEAD',
      'FOURNISSEUR DE LEAD': 'FOURNISSEUR_DE_LEAD',
      'LEAD ASSUREUR': 'RDV_LBC',
      'APPEL ENTANT': 'TELEPHONE',
      TELEPROSPECTION: 'RDV_DIRECT',
      'SITE WEB': 'INTERNET',
      APPORTEUR: 'COURTIER',
      MULTIEQUIPEMENT: 'MultiEquipement',
      'RDV LBC': 'RDV_LBC',
      'RDV DIRECT': 'RDV_DIRECT',
      'RDV AUTRES': 'RDV_DIRECT',
      TELEPHONE: 'TELEPHONE',
      'APPEL ENTRANT': 'TELEPHONE',
      DIRECT: 'MultiEquipement',
      'PARTENAIRE AUTRES': 'MultiEquipement',
      INTERNET: 'INTERNET',
      'SITE WEB BROKINS': 'INTERNET',
      COURTIER: 'COURTIER',
      'PARTENAIRE BANQUE': 'COURTIER',
      'FORCE DE VENTE': 'PARRAINAGE',
      PARRAINAGE: 'PARRAINAGE',
      DOUBLON: 'Doublon'
    };

    return aliases[normalized] ?? origine.trim();
  }

  formatDate(value?: any): string {
    if (!value) {
      return '-';
    }

    let date: Date;

    // Le backend Spring Boot renvoie parfois LocalDateTime sous forme de tableau [yyyy, MM, dd, HH, mm]
    if (Array.isArray(value)) {
      if (value.length >= 3) {
        // En JavaScript, les mois commencent à 0 (janvier = 0), on fait donc value[1] - 1
        date = new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0);
      } else {
        return '-';
      }
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      return `Aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  getAgendaCasDetailEntries(item: AgendaCas | null): Array<{ label: string; value: any }> {
    if (!item) {
      return [];
    }

    return [
      { label: 'Civilité', value: item.civilite },
      { label: 'Vendeur', value: item.ownerName },
      { label: 'RDV pris par', value: item.nomVendeur },
      { label: 'Nom', value: item.nom },
      { label: 'Téléphone', value: item.telephone },
      { label: 'Email', value: item.email },
      { label: 'Origine', value: item.origine },
      { label: 'Objet', value: item.description },
      { label: 'Commentaire', value: item.commentaire },
      { label: 'Titre agenda', value: item.title },
      { label: 'Début RDV', value: this.formatDate(item.startAt) },
      { label: 'Fin RDV', value: this.formatDate(item.endAt) },
      { label: 'ID Google event', value: item.googleEventId },
      { label: 'Statut', value: this.getStatusLabel(item.status) },
      { label: 'Projet CAS', value: item.projectId },
      { label: 'Créé le', value: this.formatDate(item.createdAt) },
      { label: 'Mis à jour le', value: this.formatDate(item.updatedAt) },
      { label: 'Payload original', value: item.rawPayload }
    ];
  }

  toggleTodayFilter(): void {
    this.todayOnly = !this.todayOnly;
    this.loadAgendaCass(0, this.rows);
  }

  toggleArchivedFilter(): void {
    this.showArchived = !this.showArchived;
    this.clearSelection();
    this.loadAgendaCass(0, this.rows);
  }

  toggleArchive(item: AgendaCas): void {
    if (this.archivingId !== null) {
      return;
    }
    const archive = !item.isArchived;
    this.archivingId = item.id;
    this.agendaCasService.updateAgendaProject(item.id, { isArchived: archive } as any).subscribe({
      next: () => {
        this.archivingId = null;
        this.removeFromSelection(item);
        this.messageService.add({
          severity: 'success',
          summary: archive ? 'RDV archivé' : 'RDV désarchivé',
          detail: archive
            ? `Le RDV de ${this.getClientName(item)} a été archivé. Retrouvez-le via le filtre « Archivés ».`
            : `Le RDV de ${this.getClientName(item)} est de nouveau actif.`
        });
        this.loadAgendaCass(0, this.rows);
      },
      error: (error: any) => {
        console.error('Erreur archivage RDV:', error);
        this.messageService.add({
          severity: 'error',
          summary: archive ? 'Archivage échoué' : 'Désarchivage échoué',
          detail: error?.error?.detail || 'Impossible de modifier l\'archivage du RDV.'
        });
        this.archivingId = null;
      }
    });
  }

  openComment(item: AgendaCas): void {
    this.commentDialogTitle = [this.getClientShortName(item), item.projectId].filter(Boolean).join(' · ');
    this.commentDialogText = (item.commentaire || '').trim();
    this.commentDialogVisible = true;
  }

  startEditDate(item: AgendaCas): void {
    this.editingItemId = item.id;
    if (item.startAt) {
      const date = new Date(item.startAt);
      if (!Number.isNaN(date.getTime())) {
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.editStartAt = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
      } else {
        this.editStartAt = '';
      }
    } else {
      this.editStartAt = '';
    }
  }

  cancelEditDate(): void {
    this.editingItemId = null;
    this.editStartAt = '';
  }

  saveDate(item: AgendaCas): void {
    if (!this.editStartAt) {
      return;
    }
    this.savingDateId = item.id;
    const newDate = new Date(this.editStartAt).toISOString();
    this.agendaCasService.updateAgendaProject(item.id, { startAt: newDate } as any).subscribe({
      next: (updated: any) => {
        this.agendaProjects = this.agendaProjects.map(p =>
          p.id === updated.id ? updated : p
        );
        this.editingItemId = null;
        this.editStartAt = '';
        this.savingDateId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Date mise à jour',
          detail: 'La date du RDV a été modifiée.'
        });
      },
      error: (error: any) => {
        console.error('Erreur mise à jour date:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Mise à jour échouée',
          detail: error?.error?.detail || 'Impossible de modifier la date du RDV.'
        });
        this.savingDateId = null;
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!this.searchTerm.trim() || !!this.selectedStatus || this.todayOnly || this.selectedUserId != null || this.showArchived;
  }

  // ---- Sélection multiple ----
  canSelectProject(item?: AgendaCas | null): boolean {
    if (!item) {
      return false;
    }
    if (this.isAdmin) {
      return true;
    }
    return this.currentUserId !== null && Number(item.userId) === this.currentUserId;
  }

  hasSelectableVisibleProjects(): boolean {
    return this.agendaProjects.some(item => this.canSelectProject(item));
  }

  isProjectSelected(item: AgendaCas): boolean {
    return this.selectedProjects.some(p => p.id === item.id);
  }

  toggleProjectSelection(item: AgendaCas, event: any): void {
    if (!this.canSelectProject(item)) {
      return;
    }
    if (event.checked) {
      if (!this.isProjectSelected(item)) {
        this.selectedProjects.push(item);
      }
    } else {
      this.removeFromSelection(item);
    }
  }

  toggleAllSelection(event: any): void {
    if (!this.hasSelectableVisibleProjects()) {
      return;
    }
    const selectableVisible = this.agendaProjects.filter(item => this.canSelectProject(item));
    if (event.checked) {
      selectableVisible.forEach(item => {
        if (!this.isProjectSelected(item)) {
          this.selectedProjects.push(item);
        }
      });
    } else {
      this.selectedProjects = this.selectedProjects.filter(selected =>
        !selectableVisible.some(visible => visible.id === selected.id)
      );
    }
  }

  areAllVisibleSelected(): boolean {
    const selectableVisible = this.agendaProjects.filter(item => this.canSelectProject(item));
    return selectableVisible.length > 0 && selectableVisible.every(item => this.isProjectSelected(item));
  }

  areSomeVisibleSelected(): boolean {
    return this.agendaProjects.some(item => this.canSelectProject(item) && this.isProjectSelected(item));
  }

  removeFromSelection(item: AgendaCas): void {
    this.selectedProjects = this.selectedProjects.filter(p => p.id !== item.id);
  }

  clearSelection(): void {
    this.selectedProjects = [];
  }

  getSelectedProjectsCount(): number {
    return this.selectedProjects.length;
  }

  // ---- Actions groupées ----
  get actionMenuOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [
      { label: 'Changer le statut de la sélection', value: 'change_status' }
    ];
    if (this.isAdmin) {
      options.push({ label: 'Attribuer la sélection', value: 'assign' });
    }
    options.push({
      label: this.showArchived ? 'Désarchiver la sélection' : 'Archiver la sélection',
      value: 'archive_selection'
    });
    options.push({ label: 'Renvoi DEVIS+SMS', value: 'sms_rappel' });
    options.push({ label: 'Exporter en Excel', value: 'export_excel' });
    options.push({ label: 'Supprimer la sélection', value: 'delete_selection' });
    return options;
  }

  onActionMenuChange(): void {
    if (this.selectedAction !== 'change_status') {
      this.selectedBulkStatus = null;
    }
    if (this.selectedAction !== 'assign') {
      this.selectedAssignUserId = null;
    }
  }

  isExecutingSelectedAction(): boolean {
    return this.updatingStatus || this.assigning || this.deletingSelection || this.exportingExcel;
  }

  canExecuteSelectedAction(): boolean {
    if (this.isExecutingSelectedAction() || this.loading) {
      return false;
    }
    // L'export peut s'exécuter sans sélection (exporte le résultat filtré).
    if (this.selectedAction === 'export_excel') {
      return true;
    }
    if (this.selectedProjects.length === 0) {
      return false;
    }
    if (this.selectedAction === 'change_status') {
      return !!this.selectedBulkStatus;
    }
    if (this.selectedAction === 'assign') {
      return this.isAdmin && !!this.selectedAssignUserId;
    }
    if (this.selectedAction === 'delete_selection' || this.selectedAction === 'export_excel' || this.selectedAction === 'sms_rappel' || this.selectedAction === 'archive_selection') {
      return true;
    }
    return false;
  }

  executeSelectedAction(): void {
    if (!this.canExecuteSelectedAction()) {
      return;
    }
    if (this.selectedAction === 'change_status') {
      this.changeSelectedStatus();
    } else if (this.selectedAction === 'assign') {
      this.assignSelected();
    } else if (this.selectedAction === 'delete_selection') {
      this.deleteSelected();
    } else if (this.selectedAction === 'export_excel') {
      this.exportToExcel();
    } else if (this.selectedAction === 'sms_rappel') {
      this.sendBulkSmsRappel();
    } else if (this.selectedAction === 'archive_selection') {
      this.archiveSelected();
    }
  }

  private archiveSelected(): void {
    if (this.selectedProjects.length === 0) {
      return;
    }
    const archive = !this.showArchived;
    const count = this.selectedProjects.length;
    this.confirmationService.confirm({
      message: archive
        ? `Archiver ${count} RDV sélectionné(s) ? Ils resteront consultables via le filtre « Archivés ».`
        : `Désarchiver ${count} RDV sélectionné(s) ? Ils redeviendront actifs.`,
      header: archive ? 'Confirmer l\'archivage' : 'Confirmer le désarchivage',
      icon: 'pi pi-inbox',
      acceptLabel: archive ? 'Archiver' : 'Désarchiver',
      rejectLabel: 'Annuler',
      accept: () => {
        this.archivingSelection = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaCasService.updateAgendaProject(item.id, { isArchived: archive } as any)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: archive ? 'RDV archivés' : 'RDV désarchivés',
              detail: archive
                ? `${count} RDV archivé(s).`
                : `${count} RDV désarchivé(s).`
            });
            this.archivingSelection = false;
            this.clearSelection();
            this.loadAgendaCass(0, this.rows);
          },
          error: (error: any) => {
            console.error('Erreur archivage groupé:', error);
            this.messageService.add({
              severity: 'error',
              summary: archive ? 'Archivage échoué' : 'Désarchivage échoué',
              detail: error?.error?.detail || 'Impossible de modifier l\'archivage des RDV sélectionnés.'
            });
            this.archivingSelection = false;
          }
        });
      }
    });
  }

  private changeSelectedStatus(): void {
    const targetStatus = this.selectedBulkStatus;
    if (!targetStatus || this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    const statusLabel = this.getStatusLabel(targetStatus);
    this.confirmationService.confirm({
      message: `Mettre ${count} RDV sélectionné(s) au statut « ${statusLabel} » ?`,
      header: 'Confirmer le changement de statut',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.updatingStatus = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaCasService.updateAgendaProject(item.id, { status: targetStatus } as any)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Statut mis à jour',
              detail: `${count} RDV mis à jour vers « ${statusLabel} ».`
            });
            this.updatingStatus = false;
            this.selectedAction = null;
            this.selectedBulkStatus = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error: any) => {
            console.error('Erreur mise à jour statut groupé:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Mise à jour échouée',
              detail: error?.error?.detail || 'Impossible de mettre à jour le statut des RDV sélectionnés.'
            });
            this.updatingStatus = false;
            this.refresh();
          }
        });
      }
    });
  }

  private assignSelected(): void {
    const targetUserId = this.selectedAssignUserId;
    if (!targetUserId || this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    const targetUser = this.userOptions.find(u => u.value === targetUserId);
    const targetLabel = targetUser?.label || 'le conseiller sélectionné';
    this.confirmationService.confirm({
      message: `Attribuer ${count} RDV sélectionné(s) à ${targetLabel} ?`,
      header: 'Confirmer l\'attribution',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.assigning = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaCasService.updateAgendaProject(item.id, { userId: targetUserId } as any)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'RDV attribués',
              detail: `${count} RDV attribué(s) à ${targetLabel}.`
            });
            this.assigning = false;
            this.selectedAction = null;
            this.selectedAssignUserId = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error: any) => {
            console.error('Erreur attribution groupée:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Attribution échouée',
              detail: error?.error?.detail || 'Impossible d\'attribuer les RDV sélectionnés.'
            });
            this.assigning = false;
            this.refresh();
          }
        });
      }
    });
  }

  private deleteSelected(): void {
    if (this.selectedProjects.length === 0) {
      return;
    }
    const count = this.selectedProjects.length;
    this.confirmationService.confirm({
      message: `Supprimer définitivement ${count} RDV sélectionné(s) ? Cette action est irréversible.`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletingSelection = true;
        const requests = this.selectedProjects.map(item =>
          this.agendaCasService.deleteAgendaProject(item.id)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'RDV supprimés',
              detail: `${count} RDV supprimé(s).`
            });
            this.deletingSelection = false;
            this.selectedAction = null;
            this.clearSelection();
            this.refresh();
          },
          error: (error: any) => {
            console.error('Erreur suppression groupée:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Suppression échouée',
              detail: error?.error?.detail || 'Impossible de supprimer les RDV sélectionnés.'
            });
            this.deletingSelection = false;
            this.refresh();
          }
        });
      }
    });
  }

  private exportToExcel(): void {
    if (this.exportingExcel) {
      return;
    }
    const selectedIds = this.selectedProjects.map(item => item.id);
    const useSelection = selectedIds.length > 0;
    const options = useSelection
      ? {}
      : {
          export_all_matching: true,
          search: this.searchTerm.trim() || undefined,
          status: this.selectedStatus || undefined,
          today_only: this.todayOnly || undefined,
          user_id: (this.isAdmin && this.selectedUserId != null) ? this.selectedUserId : undefined,
          archived: this.showArchived
        };

    this.exportingExcel = true;
    this.agendaCasService.exportAgendaProjectsExcel(useSelection ? selectedIds : [], options).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Export',
            detail: 'Aucun fichier à télécharger.'
          });
          this.exportingExcel = false;
          return;
        }
        const disposition = response.headers.get('content-disposition');
        const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null;
        const filename = match?.[1] || `rdv_sante_${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Export terminé',
          detail: useSelection
            ? `${selectedIds.length} RDV sélectionné(s) exporté(s).`
            : 'RDV santé filtrés exportés.'
        });
        this.exportingExcel = false;
        this.selectedAction = null;
      },
      error: (error: any) => {
        console.error('Erreur export Excel agenda CAS:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Export échoué',
          detail: error?.error?.detail || 'Impossible d\'exporter les RDV santé en Excel.'
        });
        this.exportingExcel = false;
      }
    });
  }

  private openCompare(item: AgendaCas, projectId?: string): void {
    this.router.navigate(['/cas/compare'], {
      queryParams: {
        nom: item.nom || undefined,
        prenom: item.prenom || undefined,
        telephone: item.telephone || undefined,
        email: item.email || undefined,
        civilite: item.civilite || undefined,
        loadProject: projectId || item.projectId || undefined
      }
    });
    this.convertingId = null;
  }

  /** Retourne le numéro E.164 (+33…) ou null si absent/invalide. */
  agendaPhoneE164(item: AgendaCas): string | null {
    const raw = (item.telephone || '').trim();
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (!digits || digits.length < 8) return null;
    if (digits.startsWith('00')) return `+${digits.slice(2)}`;
    if (digits.startsWith('33')) return `+${digits}`;
    if (digits.startsWith('0')) return `+33${digits.slice(1)}`;
    return `+${digits}`;
  }

  agendaClientName(item: AgendaCas): string {
    return [item.prenom, item.nom].filter(Boolean).join(' ') || '';
  }

  /** Retourne "Cher" ou "Chère" selon la civilité du client. */
  private buildCiviliteGreeting(civilite: string): string {
    const civ = (civilite || '').trim().toLowerCase().replace(/\./g, '');
    const feminine = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ);
    return feminine ? 'Chère' : 'Cher';
  }

  /** Normalise l'affichage de la civilité : MME -> Mme, M -> M. */
  private formatCivilite(civilite: string): string {
    const civ = (civilite || '').trim().toLowerCase().replace(/\./g, '');
    switch (civ) {
      case 'mme':
      case 'madame':
        return 'Mme';
      case 'mlle':
      case 'mademoiselle':
        return 'Mlle';
      case 'm':
      case 'mr':
      case 'monsieur':
        return 'M';
      default:
        return (civilite || '').trim();
    }
  }

  /** Construit la signature dynamique de l'agent connecté. */
  private buildAgentSignature(agent: User): string {
    const lines: string[] = [];
    const name = (agent.username || '').trim();
    if (name) lines.push(name);
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS');
    lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  getSmsBody(item: AgendaCas, agent: User): string {
    const civilite = item.civilite || '';
    const nom = (item.nom || '').trim();
    const greeting = this.buildCiviliteGreeting(civilite);
    const salutation = [greeting, this.formatCivilite(civilite), nom].filter(Boolean).join(' ');

    let heure = '';
    if (item.startAt) {
      const d = new Date(item.startAt);
      if (!Number.isNaN(d.getTime())) {
        const h = d.getHours().toString().padStart(2, '0');
        const m = d.getMinutes().toString().padStart(2, '0');
        heure = `${h}H${m}`;
      }
    }

    const now = new Date();
    const m2 = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const monthM2 = m2.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

    const sig = this.buildAgentSignature(agent);

    return [
      `${salutation},`,
      '',
      `Nous avions RDV téléphonique ce jour à ${heure} pour réaliser un bilan gratuit de votre mutuelle santé.`,
      `En tant que courtier spécialisé en Assurance Santé, nous comparons pour vous les meilleures offres du marché pour vous garantir une couverture optimale au meilleur prix.`,
      `Vous n'avez rien faire, juste 4 minutes de votre temps suffisent.`,
      `Prestation sans frais.`,
      '',
      `Renseignez-vous sur le sérieux de notre Société de Courtage.`,
      '',
      `Bien à vous`,
      '',
      sig
    ].join('\n');
  }

  openSms(item: AgendaCas): void {
    const phone = this.agendaPhoneE164(item);
    if (!phone) {
      this.messageService.add({ severity: 'warn', summary: 'Numéro manquant', detail: 'Aucun numéro de téléphone renseigné.' });
      return;
    }

    this.confirmationService.confirm({
      message: `Envoyer un SMS à ${this.getClientName(item)} (${phone}) ?`,
      header: 'Confirmer l\'envoi SMS',
      icon: 'pi pi-comment',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => this.doSendSms(item, phone)
    });
  }

  private doSendSms(item: AgendaCas, phone: string): void {
    const doSend = (agent: User) => {
      const content = this.getSmsBody(item, agent);
      this.sendingSmsId = item.id;
      this.ringoverService.sendSms({ toNumber: phone, message: content }).subscribe({
        next: () => {
          this.sendingSmsId = null;
          this.messageService.add({ severity: 'success', summary: 'SMS envoyé', detail: `SMS envoyé au ${phone}.`, life: 5000 });
        },
        error: (err: any) => {
          this.sendingSmsId = null;
          this.messageService.add({ severity: 'error', summary: 'Échec SMS', detail: err?.error?.detail || 'Erreur envoi SMS.' });
        }
      });
    };

    if (this.agentProfile) {
      doSend(this.agentProfile);
      return;
    }
    this.userService.getMyProfile().subscribe({
      next: (p) => { this.agentProfile = p; doSend(p); },
      error: () => doSend({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null })
    });
  }

  openCall(item: AgendaCas): void {
    const phone = this.agendaPhoneE164(item) || (item.telephone || '').trim();
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  }

  // --- Actions groupées: SMS Rappel Devis ---

  private buildSignature(agent: any): string {
    const lines: string[] = [];
    if (agent.username) lines.push(agent.username.trim());
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS'); lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  private formatCiviliteForSms(civilite: string | undefined): string {
    const raw = (civilite || '').trim();
    const normalized = raw.toLowerCase().replace(/\./g, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(normalized)) return 'Mme.';
    if (['m', 'mr', 'monsieur'].includes(normalized)) return 'M.';
    return raw;
  }

  private extractPhoneE164(phone: string | null | undefined): string | null {
    const raw = (phone || '').trim();
    if (!raw) return null;
    const d = raw.replace(/\D/g, '');
    if (!d || d.length < 8) return null;
    if (d.startsWith('00')) return `+${d.slice(2)}`;
    if (d.startsWith('33')) return `+${d}`;
    if (d.startsWith('0'))  return `+33${d.slice(1)}`;
    return `+${d}`;
  }

  sendBulkSmsRappel(): void {
    const projectIds = this.selectedProjects.map(p => p.id ? String(p.id) : '');
    if (!projectIds.length) return;

    this.confirmationService.confirm({
      message: `Renvoyer le devis par email et un SMS de rappel (si numéro valide) pour ${this.selectedProjects.length} RDV sélectionné(s) ?`,
      header: 'Confirmer le renvoi Devis+SMS',
      icon: 'pi pi-comment',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => {
        // 1. Envoyer les emails (Bulk Resend Devis Email)
        this.projectHistoryService.bulkResendDevisEmail(projectIds).subscribe({
          next: (res: any) => {
            this.messageService.add({ severity: 'success', summary: 'Devis envoyés', detail: `${res.sentCount || this.selectedProjects.length} email(s) envoyé(s).` });
          },
          error: (err: any) => {
            console.error('Erreur bulk devis email:', err);
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Échec de l\'envoi des devis par email.' });
          }
        });

        // 2. Envoyer les SMS
        const withPhone = this.selectedProjects.filter(p => this.extractPhoneE164(p.telephone));
        if (withPhone.length === 0) {
           return;
        }

        this.userService.getMyProfile().subscribe({
          next: (user) => {
            const sig = this.buildSignature(user || {});
            let sent = 0, failed = 0;
            const total = withPhone.length;

            withPhone.forEach(p => {
              const phone = this.extractPhoneE164(p.telephone)!;
              const nom = p.nom || '';
              const civ = p.civilite || '';
              const greeting = ['Mme', 'Mme.', 'Madame'].includes(civ) ? 'Chère' : 'Cher';
              const smsCivilite = this.formatCiviliteForSms(civ);
              const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
              const content = `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\nLe cas échéant, pouvons-nous échanger 5 minutes ?\n\nBien à vous\n\n${sig}`;
              
              this.ringoverService.sendSms({ toNumber: phone, message: content }).subscribe({
                next: () => {
                  sent++;
                  if (sent + failed === total) this.onBulkSmsDone(sent, failed);
                },
                error: () => {
                  failed++;
                  if (sent + failed === total) this.onBulkSmsDone(sent, failed);
                }
              });
            });
          },
          error: () => {
            const sig = this.buildSignature({});
            let sent = 0, failed = 0;
            const total = withPhone.length;

            withPhone.forEach(p => {
              const phone = this.extractPhoneE164(p.telephone)!;
              const nom = p.nom || '';
              const civ = p.civilite || '';
              const greeting = ['Mme', 'Mme.', 'Madame'].includes(civ) ? 'Chère' : 'Cher';
              const smsCivilite = this.formatCiviliteForSms(civ);
              const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
              const content = `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\nLe cas échéant, pouvons-nous échanger 5 minutes ?\n\nBien à vous\n\n${sig}`;
              
              this.ringoverService.sendSms({ toNumber: phone, message: content }).subscribe({
                next: () => {
                  sent++;
                  if (sent + failed === total) this.onBulkSmsDone(sent, failed);
                },
                error: () => {
                  failed++;
                  if (sent + failed === total) this.onBulkSmsDone(sent, failed);
                }
              });
            });
          }
        });
      }
    });
  }

  private onBulkSmsDone(sent: number, failed: number): void {
    if (sent > 0) {
      this.messageService.add({ severity: 'success', summary: 'SMS envoyés', detail: `${sent} SMS envoyé(s).` });
    }
    if (failed > 0) {
      this.messageService.add({ severity: 'warn', summary: 'Erreurs SMS', detail: `${failed} SMS n'ont pas pu être envoyés.` });
    }
    this.selectedAction = null;
    this.clearSelection();
  }

}
